// diagnostic-controller.js — Integration Step 2
// =============================================================================
// The headless BRIDGE between the calibrated placement engine (diagnostic-
// engine.js) and the live site. It owns three responsibilities the engine does
// NOT:
//   1. ADAPTER: turn the site's registry node/prereq/item data into the engine's
//      node shape ({id, prereqs, noQuestion}) and build the GOAL_SCOPE input
//      from a selected goal's targets.
//   2. Q&A LOOP: drive nextQuestion()/recordAnswer()/shouldStop(), translating
//      an item bank entry into a presentable question and a user's option pick
//      (or "not sure") back into 'correct'|'incorrect'|'idk'.
//   3. PLACEMENT RETURN: expose the engine's three-state readout PLUS the two
//      derived products the site needs — the VERIFIED set to seed learn.js
//      progress (decision 3, unified done Set) and ROUTING HINTS (inferredReady)
//      that app.js may use to pre-scroll/rank the path (decision 4, hints only).
//
// DESIGN: factory with INJECTED dependencies. No top-level imports of engine /
// registry / items. This is deliberate — the same file must run in TWO places:
//   * Node (headless regression, no bundler): the harness require()s the UMD
//     engine and imports graph-data + item banks, then passes them in.
//   * Browser (ES module graph): app.js imports this and passes the engine
//     global + registry ALL_NODES/ALL_PREREQS + merged ITEMS.
// Injecting deps keeps the controller environment-agnostic and unit-testable
// without a build step.
//
// SCOPE CONTRACT (matches engine decision 1): GOAL_SCOPE is SELECTION-ONLY. The
// controller does not re-implement scoping; it just forwards config.goalScope
// (a list of node ids = the selected goal's targets) to the engine. Empty/absent
// => whole-graph selection (engine default).
//
// CORE-ONLY (decision 2): frontier nodes are excluded from the engine entirely
// here — only CORE nodes are fed in. A caller that wants frontier display
// overlay handles that above the controller; the placement engine never sees a
// frontier node, so it can never ask one or place one. (Frontier nodes are also
// noQuestion by construction — no items — so even if included they'd be
// selection-inert; excluding them keeps the belief graph clean too.)
// =============================================================================

/**
 * @param {Object} deps
 *   @param {Function} deps.AtlasDiagnostic  the engine constructor (UMD or global)
 *   @param {Array}    deps.nodes            [{id, name?, cat?, layer?, ...}] — may include frontier; filtered to core
 *   @param {Array}    deps.prereqs          [[from, to, strength, layer?], ...]
 *   @param {Object}   deps.items            { nodeId: {q, opts:[4], a, concept, trap}, ... }
 * @param {Object} [config]
 *   @param {string[]} [config.goalScope]    goal target node ids -> engine GOAL_SCOPE
 *   @param {Object}   [config.engineOptions] extra engine options (MAX_QUESTIONS, LAMBDA_REACH, ...)
 *   @param {boolean}  [config.includeFrontier=false] override core-only (kept for future frontier placement)
 */
function createDiagnosticController(deps, config = {}) {
  const { AtlasDiagnostic, nodes, prereqs, items } = deps;
  if (typeof AtlasDiagnostic !== 'function') throw new Error('diagnostic-controller: AtlasDiagnostic constructor is required');
  if (!Array.isArray(nodes) || !Array.isArray(prereqs)) throw new Error('diagnostic-controller: nodes and prereqs arrays are required');
  const itemMap = items || {};

  const includeFrontier = !!config.includeFrontier;

  // ---- Core-only filtering (decision 2) ------------------------------------
  // A node counts as core when it has no layer tag or layer==='core'. Frontier
  // nodes are dropped unless explicitly overridden. We drop prereq edges that
  // reference a dropped node so the engine's byId lookups stay consistent.
  const isCore = n => includeFrontier || !n.layer || n.layer === 'core';
  const coreNodes = nodes.filter(isCore);
  const coreIds = new Set(coreNodes.map(n => n.id));

  // ---- Adapter: registry data -> engine node shape -------------------------
  // prereqs: [from, to, strength, layer?]. Engine wants, per node, the list of
  // its DIRECT prereqs (the `from` of every edge whose `to` is this node). We
  // build a from-list keyed by `to`, ignoring edges that touch a dropped node.
  const itemNodeIds = new Set(Object.keys(itemMap).filter(id => coreIds.has(id)));
  const prereqOf = new Map(coreNodes.map(n => [n.id, []]));
  for (const edge of prereqs) {
    const from = edge[0], to = edge[1];
    if (!coreIds.has(from) || !coreIds.has(to)) continue; // edge into/out of a dropped node
    prereqOf.get(to).push(from);
  }
  const engineNodes = coreNodes.map(n => ({
    id: n.id,
    prereqs: prereqOf.get(n.id).slice(),
    // A node is askable iff we authored an item for it. Everything else is
    // structural-only (noQuestion): it participates in propagation/placement but
    // is never selected as a question. This is exactly the harness adapter.
    noQuestion: !itemNodeIds.has(n.id),
  }));

  // ---- GOAL_SCOPE from selected goal targets -------------------------------
  // Forwarded verbatim to the engine (which unions each with its ancestors).
  // Unknown / frontier / typo'd targets are harmless: the engine ignores ids it
  // doesn't have. We defensively keep only core ids so a frontier goal target
  // can't silently activate an empty-but-nonzero scope.
  const goalScope = (config.goalScope || []).filter(id => coreIds.has(id));

  const engineOptions = Object.assign({}, config.engineOptions || {}, { GOAL_SCOPE: goalScope });
  const engine = AtlasDiagnostic(engineNodes, engineOptions);

  // ---- Q&A loop state ------------------------------------------------------
  let started = false;
  let currentId = null;      // node id of the question on screen
  let finished = false;
  const history = [];        // [{nodeId, outcome, pick}] audit

  function loadNext() {
    if (engine.shouldStop()) { currentId = null; finished = true; return null; }
    const id = engine.nextQuestion();
    if (id === null) { currentId = null; finished = true; return null; }
    currentId = id;
    return id;
  }

  /** The presentable question payload, or null if the diagnostic is finished. */
  function currentQuestion() {
    if (currentId === null) return null;
    const item = itemMap[currentId];
    if (!item) {
      // Defensive: engine picked a node we have no item for. Should never happen
      // (noQuestion excludes them), but if it does, treat as 'idk' and advance
      // rather than crash the loop.
      engine.recordAnswer(currentId, 'idk');
      history.push({ nodeId: currentId, outcome: 'idk', pick: null, reason: 'missing-item' });
      loadNext();
      return currentQuestion();
    }
    return {
      nodeId: currentId,
      question: item.q,
      options: item.opts.slice(),
      // NOTE: correct-answer index (item.a) is intentionally NOT exposed to the
      // UI payload — the controller grades internally in answer(). Keeps the
      // answer key out of the DOM.
      asked: engine.askedCount(),
      progress: { asked: engine.askedCount(), min: engine.opts.MIN_QUESTIONS, max: engine.opts.MAX_QUESTIONS },
    };
  }

  // ---- Public API ----------------------------------------------------------
  return {
    /** Begin the diagnostic and load the first question. Returns the first payload. */
    start() {
      if (started) return currentQuestion();
      started = true;
      loadNext();
      return currentQuestion();
    },

    /** The question currently on screen (or null when finished). */
    current() { return currentQuestion(); },

    /**
     * Grade a user's answer to the CURRENT question and advance.
     * @param {number|null} optionIndex  index into options[], or null/-1 for "not sure"
     * @returns {Object|null} the next question payload, or null when finished
     */
    answer(optionIndex) {
      if (currentId === null) return null;
      const item = itemMap[currentId];
      let outcome, pick = optionIndex;
      if (optionIndex === null || optionIndex === undefined || optionIndex < 0) {
        outcome = 'idk';
        pick = null;
      } else {
        outcome = optionIndex === item.a ? 'correct' : 'incorrect';
      }
      engine.recordAnswer(currentId, outcome);
      history.push({ nodeId: currentId, outcome, pick });
      loadNext();
      return currentQuestion();
    },

    /** Explicit "not sure" — sugar for answer(null). */
    skip() { return this.answer(null); },

    isFinished() { return finished; },

    /**
     * Final placement. Returns the engine's three-state readout PLUS derived
     * products the site consumes:
     *   verified        — asked & confirmed (hard evidence). Seeds learn.js
     *                     progress (decision 3). AUTO-marked understood.
     *   inferredMastered— lifted by propagation, never asked (>= KNOWN).
     *   inferredReady   — confirm-me tier. ROUTING HINTS only (decision 4):
     *                     used to rank/pre-scroll the path, NOT auto-marked.
     *   seedDone        — the set to write into learn.js done (== verified,
     *                     decision 4: verified-only auto-mark; inferredReady is a
     *                     hint, not a completion).
     *   routingHints    — inferredReady ids (belief-ranked) for path emphasis.
     *   asked           — audit of which nodes were actually questioned.
     */
    result() {
      const pl = engine.placement();
      return {
        // pass-through engine three-state + legacy fields
        verified: pl.verified.slice(),
        inferredMastered: pl.inferredMastered.slice(),
        inferredReady: pl.inferredReady.slice(),
        mastered: pl.mastered.slice(),
        unknown: pl.unknown.slice(),
        uncertain: pl.uncertain.slice(),
        frontier: pl.frontier.slice(),
        ready: pl.ready.slice(),
        asked: pl.asked.slice(),
        belief: pl.belief, // Map (read-only use)
        // derived products for the site
        seedDone: pl.verified.slice(),          // decision 4: verified-only auto-mark
        routingHints: pl.inferredReady.slice(), // decision 4: inferredReady = hints
      };
    },

    // ---- audit / introspection ----------------------------------------------
    history: () => history.slice(),
    goalScope: () => engine.goalScope(),
    scopeActive: () => engine.scopeActive(),
    askableCount: () => itemNodeIds.size,
    engine, // escape hatch for tests
  };
}

// UMD-ish export: ES module named export for the browser, module.exports for Node.
export { createDiagnosticController };
export default createDiagnosticController;
